/**
 * v3.99ae — Compact strain proof-stats line.
 *
 * Surfaces grow-log evidence directly on lineage cards, listing cards,
 * and strain detail pages. Pulls from `GET /api/grow-logs/stats` in
 * batched mode (see useStrainProofStats below) so list views fire ONE
 * aggregate request instead of N round-trips.
 *
 * Display contract — compact + premium (no clutter):
 *   • "3 verified grows · 1 herm report"
 *   • "5 grows logged"            (when none are yet verified)
 *   • "1 archive grow"            (when only legacy archive evidence)
 *   • Renders nothing when total === 0 unless `showEmpty` is set
 *     (the lineage detail page wants the "No grow reports yet" cue).
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Sprout, AlertTriangle } from "lucide-react";
import { api } from "../lib/apiClient";

// ---------------------------------------------------------------------------
// Context — pages that show a LIST of strain cards mount the provider once
// with the list of ids and the children read the cache without making more
// round-trips.
// ---------------------------------------------------------------------------
const StrainProofStatsContext = createContext({ map: {}, loading: true });

export function StrainProofStatsProvider({ strainIds = [], children }) {
  const [map, setMap] = useState({});
  const [loading, setLoading] = useState(true);

  // Deduplicate + stringify so the effect runs once per unique set.
  const key = useMemo(
    () => Array.from(new Set((strainIds || []).filter(Boolean))).sort().join(","),
    [strainIds],
  );

  useEffect(() => {
    if (!key) { setMap({}); setLoading(false); return; }
    let alive = true;
    setLoading(true);
    api.get("/grow-logs/stats", { params: { strain_ids: key } })
      .then((r) => { if (alive) setMap(r.data || {}); })
      .catch(() => { if (alive) setMap({}); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [key]);

  return (
    <StrainProofStatsContext.Provider value={{ map, loading }}>
      {children}
    </StrainProofStatsContext.Provider>
  );
}

/** Hook used by individual cards — pulls from the context cache if present,
 *  otherwise fetches its own single-id batch (useful on strain detail pages
 *  where no provider is mounted). */
export function useStrainProofStats(strainIdOrSlug) {
  const ctx = useContext(StrainProofStatsContext);
  const fromContext = strainIdOrSlug ? ctx?.map?.[strainIdOrSlug] : null;
  const [solo, setSolo] = useState(null);

  useEffect(() => {
    if (fromContext !== undefined && fromContext !== null) return;
    if (!strainIdOrSlug) return;
    let alive = true;
    api.get("/grow-logs/stats", { params: { strain_ids: strainIdOrSlug } })
      .then((r) => { if (alive) setSolo(r.data?.[strainIdOrSlug] || null); })
      .catch(() => { if (alive) setSolo(null); });
    return () => { alive = false; };
  }, [strainIdOrSlug, fromContext]);

  return fromContext || solo || null;
}

// ---------------------------------------------------------------------------
// Compact stat line. `size` controls density: 'card' for tiles, 'detail'
// for strain pages.
// ---------------------------------------------------------------------------
export default function StrainProofStats({ strainIdOrSlug, showEmpty = false, size = "card", testId }) {
  const stats = useStrainProofStats(strainIdOrSlug);
  if (!stats) {
    if (!showEmpty) return null;
    return (
      <span className="font-mono text-[10px] uppercase tracking-[0.18em] opacity-50"
            style={{ color: "var(--text-muted)" }}
            data-testid={testId || "proof-stats-loading"}>
        Loading proof…
      </span>
    );
  }
  if (!stats.total) {
    if (!showEmpty) return null;
    return (
      <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em]"
            style={{ color: "var(--text-muted)" }}
            data-testid={testId || `proof-stats-empty-${strainIdOrSlug}`}>
        <Sprout className="w-3 h-3 opacity-60" />
        No grow reports yet
      </span>
    );
  }

  const verifiedish = (stats.verified || 0) + (stats.community_supported || 0);
  const verifiedLabel = verifiedish > 0
    ? `${verifiedish} verified grow${verifiedish === 1 ? "" : "s"}`
    : (stats.total > 0
        ? `${stats.total} grow${stats.total === 1 ? "" : "s"} logged`
        : null);

  const isDetail = size === "detail";

  return (
    <span
      className={`inline-flex items-center gap-1.5 font-mono uppercase tracking-[0.18em] ${isDetail ? "text-xs" : "text-[10px]"}`}
      style={{ color: "var(--text-muted)" }}
      data-testid={testId || `proof-stats-${strainIdOrSlug}`}
    >
      <Sprout className="w-3 h-3" style={{ color: verifiedish > 0 ? "var(--primary-text)" : "var(--gold)" }} />
      <span style={{ color: verifiedish > 0 ? "var(--text)" : "var(--text-2)" }}>
        {verifiedLabel}
      </span>
      {(stats.herm_reports || 0) > 0 && (
        <>
          <span className="opacity-50">·</span>
          <span className="inline-flex items-center gap-0.5"
                style={{ color: "#D89393" }}
                title={`${stats.herm_reports} grow log${stats.herm_reports === 1 ? "" : "s"} reported herm pressure`}>
            <AlertTriangle className="w-3 h-3" />
            {stats.herm_reports} herm
          </span>
        </>
      )}
    </span>
  );
}
